import type { AddPlayer, AddPlayers } from '@library/types/boardBuilder';
import { groupBy, prop } from 'ramda';
import { DateTime } from 'luxon';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import { Modal } from '@library/components/modals/Modal';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getTeamName } from '@bo-utils/trading';
import { v4 as uuid } from 'uuid';

interface PlayerList extends AddPlayer {
  status: string;
}

export interface BoardBuilderUnconfirmedSelectionModalProps {
  isOpen: boolean;
  newlySelectedGameModesList: Array<FormattedGameMode>;
  newlySelectedPlayersList: AddPlayers;
  newlySelectedStatTypesList: Array<string>;
  onCancelModal: () => void;
  statTypes: Array<LeagueProjectionAttributes>;
  unSelectedGameModesList: Array<FormattedGameMode>;
  unSelectedPlayersList: AddPlayers;
  unSelectedStatTypesList: Array<string>;
}

export const BoardBuilderUnconfirmedSelectionModal = ({
  isOpen,
  newlySelectedGameModesList,
  newlySelectedPlayersList,
  newlySelectedStatTypesList,
  onCancelModal,
  statTypes,
  unSelectedGameModesList,
  unSelectedPlayersList,
  unSelectedStatTypesList
}: BoardBuilderUnconfirmedSelectionModalProps) => {
  const newlySelectedPlayers = newlySelectedPlayersList.map(
    (newlySelectedPlayer) => ({ ...newlySelectedPlayer, status: '+' })
  );

  const unSelectedPlayers = unSelectedPlayersList.map(
    (newlySelectedPlayer) => ({ ...newlySelectedPlayer, status: 'x' })
  );

  const newlySelectedStatTypes = statTypes
    .filter((statType) =>
      newlySelectedStatTypesList.includes(statType.leagueProjectionFilterId)
    )
    .map((statType) => ({
      statTypeId: statType.leagueProjectionFilterId,
      statTypeName: statType.leagueProjectionFilterName,
      status: '+'
    }));

  const unSelectedStatTypes = statTypes
    .filter((statType) =>
      unSelectedStatTypesList.includes(statType.leagueProjectionFilterId)
    )
    .map((statType) => ({
      statTypeId: statType.leagueProjectionFilterId,
      statTypeName: statType.leagueProjectionFilterName,
      status: 'x'
    }));

  const newlySelectedGameModes = newlySelectedGameModesList.map((gameMode) => ({
    ...gameMode,
    id: uuid(),
    status: '+'
  }));

  const unSelectedGameModes = unSelectedGameModesList.map((gameMode) => ({
    ...gameMode,
    id: uuid(),
    status: 'x'
  }));

  const displayStatTypes = () => {
    const allStatTypes = [...newlySelectedStatTypes, ...unSelectedStatTypes];
    if (!allStatTypes.length) {
      return null;
    }
    return (
      <div className="py-4">
        <h2 className="py-4">Stat Type Changes</h2>
        {allStatTypes.map((statType) => (
          <div className="px-4" key={`${statType.statTypeId}`}>
            <span
              className={
                statType.status === 'x'
                  ? 'text-roses pr-1'
                  : 'text-atlien-200 pr-1'
              }
            >
              {statType.status}{' '}
            </span>
            <span>{statType.statTypeName}</span>
          </div>
        ))}
      </div>
    );
  };

  const displayGameModes = () => {
    const allGameModes = [...newlySelectedGameModes, ...unSelectedGameModes];
    if (!allGameModes.length) {
      return null;
    }
    return (
      <div className="py-4">
        <h2 className="py-4">Game Mode Changes</h2>
        {allGameModes.map(({ id, label, status }) => (
          <div className="px-4" key={id}>
            <span
              className={
                status === 'x' ? 'text-roses pr-1' : 'text-atlien-200 pr-1'
              }
            >
              {status}{' '}
            </span>
            <span>{label}</span>
          </div>
        ))}
      </div>
    );
  };

  const getFormattedPlayer = (gameplayers: Array<PlayerList>) => {
    const gamePlayersList = gameplayers.map((gamePlayer) => {
      const {
        away_team: awayTeam,
        away_team_market: awayMarket,
        away_team_name: awayName,
        date_time: startTime,
        display,
        game_id: gameId,
        home_team: homeTeam,
        home_team_market: homeMarket,
        home_team_name: homeName
      } = gamePlayer.game;

      const awayTeamName = getTeamName({
        team: awayTeam,
        teamName: awayName,
        teamMarket: awayMarket
      });

      const homeTeamName = getTeamName({
        team: homeTeam,
        teamName: homeName,
        teamMarket: homeMarket
      });

      const gameDate = formatWithTimezoneChange(
        DateTime.fromISO(startTime),
        'D t ZZZZ'
      );

      const gameTitle =
        awayTeamName && homeTeamName
          ? `${awayTeamName} vs. ${homeTeamName}`
          : display;

      return {
        gameId,
        gameInfo: `${gameTitle} ${gameDate}`,
        playerId: gamePlayer.player.foreign_id,
        playerName: gamePlayer.player.name,
        status: gamePlayer.status
      };
    });

    return groupBy(prop('gameInfo'), gamePlayersList);
  };

  const displayPlayers = ({
    playersList,
    title
  }: {
    playersList: Array<PlayerList>;
    title: string;
  }) => {
    if (!playersList.length) {
      return null;
    }
    const playersObj = getFormattedPlayer(playersList);
    const gameInfos = Object.keys(playersObj);

    return (
      <div className="py-4">
        <h2 className="py-4">{title}</h2>
        <div className="px-4">
          {gameInfos.map((gameInfo) => (
            <div key="gameInfo">
              <div className="pb-4 font-bold">{gameInfo}</div>
              {playersObj[gameInfo]?.map(
                ({ gameId, playerId, playerName, status }) => (
                  <div className="px-4" key={`${gameId}-${playerId}`}>
                    <span
                      className={
                        status === 'x'
                          ? 'text-roses pr-1'
                          : 'text-atlien-200 pr-1'
                      }
                    >
                      {status}{' '}
                    </span>
                    <span>{playerName}</span>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onCancelModal={onCancelModal}>
      <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto max-h-[650px] w-[55rem] flex-col overflow-scroll rounded-md border px-8 py-6">
        {displayStatTypes()}
        {displayGameModes()}
        {displayPlayers({
          playersList: [...newlySelectedPlayers, ...unSelectedPlayers],
          title: 'Player Changes'
        })}
      </div>
    </Modal>
  );
};
